/*----------------------------------
- DOMAINS LANDING SEO COPY CLEANUP
----------------------------------*/

import type {
    WebsiteDomainsLandingResponse,
    WebsiteDomainsLandingSeo,
} from '@/common/types/domains/websiteLanding';

/*----------------------------------
- HELPERS
----------------------------------*/

/** An em dash (U+2014) with the spaces around it, the way the copy model writes it. */
const EM_DASH_PATTERN = /\s*\u2014\s*/g;
/** Two commas the replacement can leave side by side, when a comma already stood before the dash. */
const DOUBLED_COMMA_PATTERN = /,(\s*,)+/g;
/** A comma the replacement leaves at either end of the text. */
const EDGE_COMMA_PATTERN = /^[\s,]+|[\s,]+$/g;

/**
 * The search copy of a `/domains*` page is written by the copy model once per search and stored,
 * so it can carry the em dash the project bans from every text (AGENTS.md). The website prints it
 * verbatim, so it is cleaned here, once, before it reaches the title, the h1, the intro, the FAQ,
 * the JSON-LD or the Markdown mirror: each em dash becomes a comma.
 *
 * @fix  docs/fixes/2026-10-11-domains-seo-copy-em-dash.md
 * @rule Every website surface prints `search.seo` only through `withCleanSearchSeoCopy`.
 */
export const removeEmDashes = (text: string): string => {
    if (!text.includes('\u2014')) return text;

    return text
        .replace(EM_DASH_PATTERN, ', ')
        .replace(DOUBLED_COMMA_PATTERN, ',')
        .replace(EDGE_COMMA_PATTERN, '');
};

const cleanSeo = (seo: WebsiteDomainsLandingSeo): WebsiteDomainsLandingSeo => ({
    metadata: {
        title_tag: removeEmDashes(seo.metadata.title_tag ?? ''),
        meta_description: removeEmDashes(seo.metadata.meta_description ?? ''),
        og_title: removeEmDashes(seo.metadata.og_title ?? ''),
        og_description: removeEmDashes(seo.metadata.og_description ?? ''),
    },
    page: {
        h1: removeEmDashes(seo.page.h1 ?? ''),
        header_subtitle: removeEmDashes(seo.page.header_subtitle ?? ''),
        answer_snippet: removeEmDashes(seo.page.answer_snippet ?? ''),
        section_title: removeEmDashes(seo.page.section_title ?? ''),
        intro_paragraph: removeEmDashes(seo.page.intro_paragraph ?? ''),
        bullets: (seo.page.bullets ?? []).map(removeEmDashes),
        faqs: (seo.page.faqs ?? []).map((faq) => ({
            ...faq,
            q: removeEmDashes(faq.q ?? ''),
            a: removeEmDashes(faq.a ?? ''),
        })),
    },
});

/*----------------------------------
- EXPORTS
----------------------------------*/

/** The search with its stored SEO copy cleaned of em dashes; every other field is untouched. */
export function withCleanSearchSeoCopy(search: WebsiteDomainsLandingResponse): WebsiteDomainsLandingResponse;
export function withCleanSearchSeoCopy(search: WebsiteDomainsLandingResponse | null): WebsiteDomainsLandingResponse | null;
export function withCleanSearchSeoCopy(search: WebsiteDomainsLandingResponse | null): WebsiteDomainsLandingResponse | null {
    if (!search?.seo) return search;
    return { ...search, seo: cleanSeo(search.seo) };
}
